/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Alert,
    Button,
    Stack,
    Text,
    Link as ChakraLink
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import RegistrationFields from '../../components/forms/registration-fields'
import {useLocation} from 'react-router-dom'
import {AccountDrawerLogoGarage, AccountDrawerLogoDynamite} from '../icons'
import {useBrand} from '../../contexts/brand-context'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {Flex} from '@salesforce/retail-react-app/app/components/shared/ui'
import SocialLogin from '../../components/social-login'
import GoogleRecatpchaText from '../google-recatpcha-text'

// MRT multi site
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'

const RegisterForm = ({
    submitForm,
    clickSignIn = noop,
    form,
    prefix = '',
    isSocialEnabled = false,
    idps = []
}) => {
    const {isGarage} = useBrand()
    const {app: appConfig} = getConfig()
    const location = useLocation()
    const navigate = useNavigation()
    const isRegistrationPage = location.pathname.endsWith('/registration')
    const {site} = useMultiSite()
    const siteId = site?.id
    const enabledIDPs = getSiteEnvVarValue(appConfig, 'SFID_SOCIAL_LOGIN_ENABLED_IDPS', siteId)
    const hasSocialOptions =
        enabledIDPs && enabledIDPs.trim() && enabledIDPs.toLowerCase() !== 'false'
    const shouldShowSocial = isSocialEnabled && hasSocialOptions && idps.length > 0

    const isDuplicateCustomerError = form.formState.errors?.global?.isDuplicateCustomer
    const handleSignInClick = () => {
        if (isRegistrationPage) {
            navigate('/login')
            return
        }
        clickSignIn()
    }

    return (
        <Fragment>
            <Stack justify="center" align="center" spacing={4} my={8}>
                {isGarage ? (
                    <>
                        <AccountDrawerLogoGarage width="110px" height="auto" />
                        <Text align="center" fontSize="md" fontWeight="medium">
                            <FormattedMessage
                                defaultMessage="Join, earn points, get spoiled"
                                id="login_form.message.join.garage"
                            />
                        </Text>
                    </>
                ) : (
                    <>
                        <AccountDrawerLogoDynamite width="110px" height="auto" />
                        <Text align="center" fontSize="md" fontWeight="regular">
                            <FormattedMessage
                                defaultMessage="Join, earn points, get spoiled"
                                id="login_form.message.join.dynamite"
                            />
                        </Text>
                    </>
                )}
            </Stack>
            <form
                onSubmit={form.handleSubmit(submitForm)}
                data-testid="sf-auth-modal-form-register"
                noValidate
            >
                <Stack spacing={8} px={isRegistrationPage ? {base: 4, lg: 0} : 0}>
                    {form.formState.errors?.global && (
                        <Alert status="error">
                            <AlertIcon color="red.500" boxSize={4} />
                            <Text fontSize="sm" ml={3}>
                                {isDuplicateCustomerError ? (
                                    isGarage ? (
                                        <FormattedMessage
                                            id="registration.error.duplicate_customer.garage"
                                            defaultMessage="This email address already exists. Please try again or <signIn>sign in</signIn>. If the issue persists, please contact <customerService>Customer Service</customerService>."
                                            values={{
                                                signIn: (chunks) => (
                                                    <ChakraLink
                                                        as="button"
                                                        type="button"
                                                        onClick={handleSignInClick}
                                                        textDecoration="underline"
                                                        color="inherit"
                                                    >
                                                        {chunks}
                                                    </ChakraLink>
                                                ),
                                                customerService: (chunks) => (
                                                    <ChakraLink
                                                        href={getSiteEnvVarValue(
                                                            appConfig,
                                                            'CUSTOMER_SERVICE_URL',
                                                            siteId
                                                        )}
                                                        textDecoration="underline"
                                                        color="inherit"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {chunks}
                                                    </ChakraLink>
                                                )
                                            }}
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="registration.error.duplicate_customer.dynamite"
                                            defaultMessage="This email address already exists. Please try again or <signIn>sign in</signIn>. If the issue persists, please contact <customerService>Customer Service</customerService>."
                                            values={{
                                                signIn: (chunks) => (
                                                    <ChakraLink
                                                        as="button"
                                                        type="button"
                                                        onClick={handleSignInClick}
                                                        textDecoration="underline"
                                                        color="inherit"
                                                    >
                                                        {chunks}
                                                    </ChakraLink>
                                                ),
                                                customerService: (chunks) => (
                                                    <ChakraLink
                                                        href={getSiteEnvVarValue(
                                                            appConfig,
                                                            'CUSTOMER_SERVICE_URL',
                                                            siteId
                                                        )}
                                                        textDecoration="underline"
                                                        color="inherit"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {chunks}
                                                    </ChakraLink>
                                                )
                                            }}
                                        />
                                    )
                                ) : (
                                    form.formState.errors.global.message
                                )}
                            </Text>
                        </Alert>
                    )}
                    <RegistrationFields form={form} prefix={prefix} />
                    <Stack spacing={4}>
                        <Button
                            variant="webPrimary"
                            type="submit"
                            data-testid="register-join-collectif-button"
                            onClick={() => form.clearErrors('global')}
                            isLoading={form.formState.isSubmitting}
                            _focusVisible={{
                                outline: '2px solid',
                                outlineColor: 'webTheme.focus',
                                outlineOffset: '2px'
                            }}
                        >
                            {isGarage ? (
                                <FormattedMessage
                                    defaultMessage="Join our Collectif"
                                    id="login_join.our.collectif.garage"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Join our Collectif"
                                    id="login_join.our.collectif.dynamite"
                                />
                            )}
                        </Button>
                        <GoogleRecatpchaText />
                    </Stack>

                    {shouldShowSocial && idps.length > 0 && (
                        <Stack spacing={2}>
                            <Text fontSize="sm" fontWeight="sm">
                                <FormattedMessage
                                    defaultMessage="Or Sign In With"
                                    id="contact_info.message.or_sign_in_with"
                                />
                            </Text>
                            <Flex justifyContent="center" gap="30px">
                                <SocialLogin form={form} idps={idps} enabledIDPs={enabledIDPs} />
                            </Flex>
                        </Stack>
                    )}
                    <Stack spacing={8}>
                        <Stack direction="row" spacing={1} justify="flex-start">
                            <Text fontSize={{base: 'xs', lg: 'sm'}}>
                                {isGarage ? (
                                    <FormattedMessage
                                        defaultMessage="Already have an account?"
                                        id="register_form.message.already_have_account.garage"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Already have an account?"
                                        id="register_form.message.already_have_account.dynamite"
                                    />
                                )}
                            </Text>
                            <Button
                                variant="link"
                                size="xs"
                                fontSize={{base: 'xs', lg: 'sm'}}
                                onClick={handleSignInClick}
                            >
                                <FormattedMessage
                                    defaultMessage="Sign in"
                                    id="register_form.action.sign_in"
                                />
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </form>
        </Fragment>
    )
}

RegisterForm.propTypes = {
    submitForm: PropTypes.func,
    clickSignIn: PropTypes.func,
    form: PropTypes.object,
    prefix: PropTypes.string,
    isSocialEnabled: PropTypes.bool,
    idps: PropTypes.array
}

export default RegisterForm
